import React from 'react'
import {
	Card,
	Form,
	Button,
	Typography,
	Row,
	Col,
	DatePicker,
	InputNumber,
	Table,
	Modal,
	Space,
	Tag,
	Tooltip,
} from 'antd'
import { CalendarOutlined, EyeOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'

const { Title, Text } = Typography

interface ShiftConfigTabProps {
	shiftType: string
	typeInfo: { name: string; color: string }
	shiftConfig: any
	shiftConfigForm: any
	loadingShift: boolean
	isModalVisible: boolean
	modalMode: 'view' | 'create' | 'edit'
	onView: () => void
	onEdit: () => void
	onCreate: () => void
	onModalOk: () => void
	onModalCancel: () => void
}

const ShiftConfigTab: React.FC<ShiftConfigTabProps> = ({
	shiftType,
	typeInfo,
	shiftConfig,
	shiftConfigForm,
	loadingShift,
	isModalVisible,
	modalMode,
	onView,
	onEdit,
	onCreate,
	onModalOk,
	onModalCancel,
}) => {
	const columns = [
		{
			title: 'Shift Type',
			key: 'shiftType',
			width: 120,
			render: () => <Tag color={typeInfo.color}>{typeInfo.name}</Tag>,
		},
		{
			title: 'Total Groups',
			dataIndex: 'totalGroup',
			key: 'totalGroup',
			width: 150,
		},
		{
			title: 'Start From Group',
			dataIndex: 'startFromGroup',
			key: 'startFromGroup',
			width: 150,
		},
		{
			title: 'Start Date',
			dataIndex: 'startDate',
			key: 'startDate',
			width: 150,
		},
		{
			title: 'Status',
			key: 'status',
			width: 100,
			render: () => <Tag color='green'>Active</Tag>,
		},
		{
			title: 'Action',
			key: 'action',
			width: 200,
			render: () => (
				<Space size='small'>
					<Button type='link' icon={<EyeOutlined />} onClick={onView}>
						View
					</Button>
					<Button type='link' icon={<EditOutlined />} onClick={onEdit}>
						Update
					</Button>
				</Space>
			),
		},
	]

	const dataSource = shiftConfig ? [{ ...shiftConfig, key: '1' }] : []

	return (
		<>
			<Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
				<div
					style={{
						marginBottom: 16,
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<div>
						<Title level={5} style={{ margin: 0 }}>
							<CalendarOutlined /> {typeInfo.name} Shift Configuration
						</Title>
						<Text type='secondary'>
							Manage the shift rotation schedule for your organization.
						</Text>
					</div>
					<Tooltip
						title={
							shiftConfig
								? "Create button is enabled when there's no shift configuration only"
								: ''
						}
					>
						<Button
							type='primary'
							icon={<PlusOutlined />}
							onClick={onCreate}
							disabled={!!shiftConfig}
						>
							Create Configuration
						</Button>
					</Tooltip>
				</div>

				<Table
					columns={columns}
					dataSource={dataSource}
					pagination={false}
					locale={{
						emptyText: (
							<div style={{ padding: '40px 0' }}>
								<Text type='secondary'>
									No shift configuration found. Click "Create Configuration" to
									add one.
								</Text>
							</div>
						),
					}}
				/>
			</Card>

			<Modal
				title={
					modalMode === 'view'
						? `View ${typeInfo.name} Shift Configuration`
						: modalMode === 'edit'
							? `Update ${typeInfo.name} Shift Configuration`
							: `Create ${typeInfo.name} Shift Configuration`
				}
				open={isModalVisible}
				onOk={onModalOk}
				onCancel={onModalCancel}
				width={700}
				okText={modalMode === 'view' ? 'Close' : modalMode === 'edit' ? 'Update' : 'Create'}
				confirmLoading={loadingShift}
				cancelButtonProps={{
					style: { display: modalMode === 'view' ? 'none' : 'inline-block' },
				}}
			>
				<Form form={shiftConfigForm} layout='vertical' disabled={modalMode === 'view'}>
					<Row gutter={16}>
						<Col xs={24} md={8}>
							<Form.Item
								name='totalGroup'
								label='Total Groups'
								rules={[
									{
										required: true,
										message: 'Please enter total groups',
									},
									{
										type: 'number',
										min: 1,
										message: 'Must be at least 1',
									},
								]}
							>
								<InputNumber
									style={{ width: '100%' }}
									min={1}
									placeholder='e.g., 4'
									size='large'
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item
								name='startFromGroup'
								label='Start From Group'
								rules={[
									{
										required: true,
										message: 'Please enter start group',
									},
									{
										type: 'number',
										min: 1,
										message: 'Must be at least 1',
									},
								]}
							>
								<InputNumber
									style={{ width: '100%' }}
									min={1}
									placeholder='e.g., 1'
									size='large'
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item
								name='startDate'
								label='Start Date'
								rules={[
									{
										required: true,
										message: 'Please select start date',
									},
								]}
							>
								<DatePicker
									style={{ width: '100%' }}
									format='MM/DD/YYYY'
									placeholder='Select date'
									size='large'
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Modal>
		</>
	)
}

export default ShiftConfigTab
